import { transformObjectValuesArrayToSingle } from 'utils';
import { DateTime } from 'luxon';
import i18next from 'i18next';
import { roundToTwoDecimals } from 'utils/roundToTwoDecimals';
import { getCurrentlySelectedLocation } from 'utils/getCurrentlySelectedLocation';

export const range = (start: number, end: number): number[] => {
  if (start === end) return [start];
  return [start, ...range(start + 1, end)];
};

export const getSeconds = (_date: string, startOfDay?: boolean): number => {
  let compareDate;
  if (startOfDay) {
    compareDate = DateTime.fromISO(_date).startOf('day');
  } else {
    compareDate = DateTime.now();
  }
  const date = _date ? DateTime.fromISO(_date) : DateTime.now();
  return Math.abs(date.diff(compareDate, 'second').seconds);
};

export const getSecondsForWholeDay = (): number => {
  const startOfDay = DateTime.now().startOf('day');
  const endOfDay = DateTime.now().endOf('day');
  return endOfDay.diff(startOfDay, 'second').seconds;
};

export const recurringMessage = (interval: number, frequency: string): string => {
  switch (frequency) {
    case 'day':
      if (interval === 1) return 'Every day';
      return `Every ${interval} days`;
    case 'week':
      if (interval === 1) return 'Every week';
      return `Every ${interval} weeks`;
    case 'month':
      if (interval === 1) return 'Every month';
      return `Every ${interval} months`;
    default:
      return '';
  }
};

export const breakTimeToWords = (breaktime: string | null): string => {
  if (breaktime == null) {
    return '';
  }

  const hour = breaktime.split(':')[0];
  const min = breaktime.split(':')[1];
  if (hour === '01') {
    return '1 hour';
  }
  return `${Number(min)} mins`;
};

export const formatColonToWords = (time: string): string => {
  if (!time) return '-';

  // Changes time format from 1:30 to 1 hr and 30 mins
  const hours = time.split(':')[0];
  const mins = time.split(':')[1];

  let formattedTime = '';
  if (hours === '1') {
    formattedTime = '1 hr ';
  } else if (hours !== '0' && hours !== '1') {
    formattedTime += `${hours} hrs `;
  }

  if ((mins as unknown as number) !== 0) {
    formattedTime += `${mins.toString().substring(0, 2)} mins`;
  }

  return formattedTime !== '' ? formattedTime : '-';
};

export const formatDotToWords = (time: string): string => {
  if (!time) return '-';

  // Changes time format from 1.5 to 1 hr and 30 mins
  const hours = time.split('.')[0];
  const mins = (Number(time.split('.')[1]) / 10) * 60;

  let formattedTime = '';
  if (hours === '1') {
    formattedTime = '1 hr ';
  } else if (hours !== '0' && hours !== '1') {
    formattedTime += `${hours} hrs `;
  }

  if (mins !== 0) {
    formattedTime += `${mins.toString().substring(0, 2)} mins`;
  }

  return formattedTime !== '' ? formattedTime : '-';
};

export const formatDotToFullWords = (time: string): string => {
  // Changes time format from 1.5 to 1 hr and 30 mins
  const hours = time.split('.')[0];
  const mins = ((Number(time.split('.')[1]) || 0) / 10) * 60;

  let formattedTime = '';
  if (hours === '1') {
    formattedTime = '1 hour ';
  } else if (hours !== '0' && hours !== '1') {
    formattedTime += `${hours} hours `;
  }

  if (mins !== 0) {
    formattedTime += `${mins.toString().substring(0, 2)} mins`;
  } else {
    formattedTime += '00 mins';
  }

  return formattedTime !== '' ? formattedTime : '-';
};

export function convertTimeToHours(time: string): number {
  const [hours, minutes] = time.split(':');
  const convertMinutes = parseInt(String((Number(minutes) / 6) * 10), 10);

  return parseFloat(`${parseInt(hours, 10)}.${convertMinutes < 10 ? '0' : ''}${convertMinutes}`);
}

export const getPluralizedTimeUnitString = (
  value: string | number,
  unit: string,
  returnZeroValue = false,
): string => {
  if (!returnZeroValue && value === '0') {
    return '';
  }
  return `${value} ${unit}${value === '1' ? '' : 's'}`;
};

export function formatInterval(minutes: number): string {
  if (minutes === 0) {
    return `0 mins`;
  }

  const interval = [
    roundToTwoDecimals(Math.floor(minutes / 60)).toString(),
    roundToTwoDecimals(minutes % 60)
      .toString()
      .padStart(2, '0'),
  ];

  if ((minutes % 60).toString() === '0') return getPluralizedTimeUnitString(interval[0], 'hr');

  return `${getPluralizedTimeUnitString(interval[0], 'hr')} ${getPluralizedTimeUnitString(
    interval[1],
    'min',
    true,
  )}`;
}

export function convertTimeToString(hour: number): string {
  return DateTime.now().set({ hour: 0, minute: 0 }).plus({ hours: hour }).toFormat('HH:mm');
}

export function calculateDuration(
  startTime: string,
  endTime: string,
  startDate: string,
  unpaidBreak: number,
  format: 'raw',
  withUnpaid?: boolean,
): number;
export function calculateDuration(
  startTime: string,
  endTime: string,
  startDate: string,
  unpaidBreak: number,
  format?: string,
  withUnpaid?: boolean,
): string | number;
export function calculateDuration(
  startTime: string,
  endTime: string,
  startDate: string,
  unpaidBreak: number,
  format = 'pretty',
  withUnpaid = true,
): string | number {
  const location = getCurrentlySelectedLocation();
  const time_zone = location.timeZone;
  const unPaidToMinutes = (withUnpaid ? unpaidBreak : 0) * 60;
  const [hoursA, minutesA] = startTime.split(':');
  const [hoursB, minutesB] = endTime.split(':');

  const timeA = DateTime.fromISO(startDate)
    .setZone(time_zone)
    .set({ hour: Number(hoursA), minute: Number(minutesA) });

  let timeB = DateTime.fromISO(startDate)
    .setZone(time_zone)
    .set({ hour: Number(hoursB), minute: Number(minutesB) });

  if (timeB < timeA) {
    timeB = timeB.plus({ day: 1 });
  }

  const interval = timeB.diff(timeA, 'minute').as('minutes');
  let formula = interval - unPaidToMinutes;
  if (interval < 0) {
    formula = 24 * 60 + timeB.diff(timeA, 'minutes').minutes - unPaidToMinutes;
  }
  if (formula < 0) formula = 0;

  return format === 'pretty' ? formatInterval(formula) : roundToTwoDecimals(formula);
}

export const getHourMinuteSecond = (hourAndMinString: string): number[] => {
  return hourAndMinString.split(':').map((str) => parseInt(str));
};

export const getShiftStartTimeAndEndTime = (
  args: Record<string, string | string[]>,
): [string, string] => {
  const { startDate, startAt, endAt } = transformObjectValuesArrayToSingle(args);
  const _startDate = startDate ? DateTime.fromISO(startDate) : DateTime.now();
  const [startHour, startMinute] = getHourMinuteSecond(startAt);
  const [endHour, endMinute] = getHourMinuteSecond(endAt);
  const start_time = _startDate.set({
    hour: startHour,
    minute: startMinute,
    second: 0,
    millisecond: 0,
  });

  let end_time = DateTime.fromISO(String(_startDate)).set({
    hour: endHour,
    minute: endMinute,
    second: 0,
    millisecond: 0,
  });

  if (start_time > end_time) {
    end_time = end_time.plus({ day: 1 });
  }

  return [start_time.toString(), end_time.toString()];
};

export const calculateShiftDurationInMinutes = (
  args: Record<string, string | string[] | boolean>,
): number => {
  const { startDate, startAt, endAt, unpaidBreakTime, isPaidDuration } =
    transformObjectValuesArrayToSingle(args);
  const [start_time, end_time] = getShiftStartTimeAndEndTime({ startDate, startAt, endAt });
  let duration = DateTime.fromISO(end_time).diff(DateTime.fromISO(start_time), 'minute').minutes;

  if (isPaidDuration) {
    const unpaidBreakHours = unpaidBreakTime ? convertTimeToHours(unpaidBreakTime) : 0;
    duration = duration - unpaidBreakHours * 60;
  }

  if (duration < 0) {
    duration = 0;
  }

  return duration;
};

// duration calculation
export const durationInHours = (
  t1: string,
  t2: string,
  date: string,
  breaktime: string,
  duration: string,
): number => {
  calculateDuration(t1, t2, date, convertTimeToHours(breaktime));
  const b = convertTimeToHours(breaktime);
  const x = DateTime.fromISO(t1);
  let y = DateTime.fromISO(t2);
  if (x > y) {
    y = y.plus({ day: 1 });
  }
  // subtract break time in minutes
  return (
    ((y.diff(x).as(duration as 'minutes' | 'hours') as unknown as Record<string, number>)[
      'duration'
    ] -
      b) /
    60
  );
};

export const totalHoursFromDate = (date: string): number => {
  const selectedDate = DateTime.fromISO(date);

  // Calculate the duration in hours till now
  return selectedDate.diffNow('hour').get('hour');
};

export const combineDateTime = (date: string, time: string): string => {
  const parsedDate = DateTime.fromISO(date);
  // Extract the date part and combine it with newTime
  const combinedDateTime = parsedDate.toFormat('yyyy-LL-dd') + 'T' + time;
  // Retain timeZone
  return combinedDateTime + parsedDate.toFormat('Z');
};

interface TimeInterval {
  key: string;
  value: string;
  text: string;
}

export const getQuarterHoursInDay = (is24h: boolean): TimeInterval[] => {
  const intervals: TimeInterval[] = [];

  if (is24h) {
    for (let i = 0; i < 24; i += 1) {
      for (let quarterHour = 0; quarterHour < 1; quarterHour += 0.25) {
        let formattedQuarterHour: string;
        switch (quarterHour) {
          case 0.25:
            formattedQuarterHour = `${i < 10 ? '0' : ''}${i}:15`;
            break;
          case 0.5:
            formattedQuarterHour = `${i < 10 ? '0' : ''}${i}:30`;
            break;
          case 0.75:
            formattedQuarterHour = `${i < 10 ? '0' : ''}${i}:45`;
            break;
          default:
            formattedQuarterHour = `${i < 10 ? '0' : ''}${i}:00`;
            break;
        }
        intervals.push({
          key: formattedQuarterHour,
          value: formattedQuarterHour,
          text: formattedQuarterHour,
        });
      }
    }
    return intervals;
  }

  intervals.push(
    {
      key: '12:00 am',
      value: '00:00',
      text: '12:00 am',
    },
    {
      key: '12:15 am',
      value: '00:15',
      text: '12:15 am',
    },
    {
      key: '12:30 am',
      value: '00:30',
      text: '12:30 am',
    },
    {
      key: '12:45 am',
      value: '00:45',
      text: '12:45 am',
    },
  );

  for (let i = 1; i < 12; i += 1) {
    for (let quarterHour = 0; quarterHour < 1; quarterHour += 0.25) {
      let formattedQuarterHour: string;
      switch (quarterHour) {
        case 0.25:
          formattedQuarterHour = `${i}:15`;
          break;
        case 0.5:
          formattedQuarterHour = `${i}:30`;
          break;
        case 0.75:
          formattedQuarterHour = `${i}:45`;
          break;
        default:
          formattedQuarterHour = `${i}:00`;
          break;
      }
      intervals.push({
        key: `${formattedQuarterHour} am`,
        value: formattedQuarterHour,
        text: `${formattedQuarterHour} am`,
      });
    }
  }

  intervals.push(
    {
      key: '12:00 pm',
      value: '12:00',
      text: '12:00 pm',
    },
    {
      key: '12:15 pm',
      value: '12:15',
      text: '12:15 pm',
    },
    {
      key: '12:30 pm',
      value: '12:30',
      text: '12:30 pm',
    },
    {
      key: '12:45 pm',
      value: '12:45',
      text: '12:45 pm',
    },
  );
  for (let i = 1; i < 12; i += 1) {
    for (let quarterHour = 0; quarterHour < 1; quarterHour += 0.25) {
      let formattedQuarterHour: string;
      let valueQuarterHour: string;
      switch (quarterHour) {
        case 0.25:
          formattedQuarterHour = `${i}:15`;
          valueQuarterHour = `${i + 12}:15`;
          break;
        case 0.5:
          formattedQuarterHour = `${i}:30`;
          valueQuarterHour = `${i + 12}:30`;
          break;
        case 0.75:
          formattedQuarterHour = `${i}:45`;
          valueQuarterHour = `${i + 12}:45`;
          break;
        default:
          formattedQuarterHour = `${i}:00`;
          valueQuarterHour = `${i + 12}:00`;
          break;
      }
      intervals.push({
        key: `${formattedQuarterHour} pm`,
        value: valueQuarterHour,
        text: `${formattedQuarterHour} pm`,
      });
    }
  }

  return intervals;
};

export const durationLongerThanHour = (duration: number): boolean => {
  // -0.25 is 24 hour edge case (start-end-breaktime).
  return duration > 1 || duration === -0.25;
};

export const formatTimePickerValue = (time: string): string => {
  return time.length < 5 ? `0${time}` : time;
};

export function getHoursAndMinutesFromMins(mins: number): string {
  if (mins) {
    const hours = mins / 60;
    const rmins = Math.floor(hours) - mins;
    return `${hours ? `${hours} hours` : ''} ${rmins ? `${rmins} mins` : ''}`;
  }

  return '0 mins';
}

export const getHoursAndMinsFromDate = (date: string, time_zone: string): string => {
  return DateTime.fromISO(date).setZone(time_zone).toFormat('HH:mm');
};

export function getDaysHoursAndMinutesFromMin(mins: number): string {
  const hours = Math.floor(mins / 60);
  const rminutes = mins % 60;
  const days = Math.floor(hours / 24);
  const rhours = hours % 24;
  if (days || rhours || rminutes) {
    return `${days ? `${days} ${days === 1 ? 'day' : 'days'}` : ''}  ${
      rhours ? `${rhours} ${rhours === 1 ? 'hr' : 'hrs'}` : ''
    }  ${rminutes ? `${rminutes} mins` : ''} `.trim();
  }
  return `0 mins`;
}

export function getHoursAndMinutesFromHoursFloat(hours: number, type = 'mins'): string {
  const rhours = Math.floor(hours);
  const minutes = (hours - rhours) * 60;
  const rminutes = Math.round(minutes);
  if (rhours || rminutes) {
    return `${rhours ? `${rhours} ${rhours === 1 ? 'hr' : 'hrs'}` : ''}  ${
      rminutes ? `${rminutes} mins` : ''
    } `.trim();
  }
  return `0 ${type}`;
}
export function getWeeks(weeks: number, type = 'week'): string {
  if (weeks) {
    return `${
      weeks ? `${weeks} ${weeks === 1 ? i18next.t('WEEK') : i18next.t('WEEKS')}` : ''
    }`.trim();
  }
  return `1 ${type}`;
}
export const mapArrayForDropDownWeek = (weeksArray: number[], type?: string) =>
  weeksArray.map((option) => ({
    key: option,
    value: option,
    text: getWeeks(option, type),
  }));
export const mapArrayForDropDownTimes = (timesArray: number[], type?: string) =>
  timesArray.map((option) => ({
    key: option,
    value: option,
    text: getHoursAndMinutesFromHoursFloat(option, type),
  }));

export const mapArrayForDropDownHours = (hoursArray: number[]) =>
  hoursArray.map((option) => ({
    key: option,
    value: option,
    text: `${option} ${option === 1 ? 'hour' : 'hours'}`,
  }));

export const mapArrayForDropDownMinutes = (minutesArray: number[]) =>
  minutesArray.map((option) => ({
    key: option,
    value: option,
    text: `${option} ${option === 1 ? 'minute' : 'minutes'}`,
  }));

export const mapArrayForDropDownTimesString = (timesArray: number[], type?: string) => {
  return timesArray.map((option) => {
    const day = DateTime.now().set({ hour: 0, minute: 0, second: 0 });
    return {
      key: option,
      value: day.plus({ hour: option }).toFormat('HH:mm'),
      text: getHoursAndMinutesFromHoursFloat(option, type),
    };
  });
};

export const mapArrayForBKJDropDownTimes = (timesArray: number[], type?: string) =>
  timesArray.map((option) => ({
    key: option,
    value: option,
    label: getHoursAndMinutesFromHoursFloat(option, type),
  }));

export const generateTime = (hour: number): number[] => {
  let value = 0;
  const minsArray: number[] = [];
  const totalMinutes = hour * 60;
  while (value !== totalMinutes) {
    minsArray.push(value / 60.0);
    value += 15;
  }
  return minsArray;
};

export const generateMinsArray = (
  hours: number,
  withMinutes = false,
  minuteStopper: number | null = 1,
): number[] => {
  if (withMinutes) {
    return [...generateTime(minuteStopper || hours), ...range(minuteStopper || hours, hours)];
  }
  return [...range(minuteStopper || 0, hours)];
};

export const timeDropdown = (is24h: boolean) => {
  const hourIncrement = generateMinsArray(24, true, 24);
  const day = DateTime.now().set({ hour: 0, minute: 0, second: 0 });
  hourIncrement.pop();
  return hourIncrement.map((hour) => {
    return {
      key: day.plus({ hour }).toFormat('HH:mm'),
      value: day.plus({ hour }).toFormat('HH:mm'),
      label: day.plus({ hour }).toFormat(is24h ? 'HH:mm' : 'h:mm a'),
    };
  });
};

export function getTimeZone(): string {
  return DateTime.now().zoneName;
}

export const getHoursMinsSecFromDate = (date: string): string => {
  return DateTime.fromISO(date).toFormat('HH:mm:ss');
};

export const isDateBeforeNow = (date: string): boolean => {
  const [hours, minutes] = getHoursMinsSecFromDate(date).split(':');
  return (
    DateTime.fromISO(date).set({ hour: Number(hours), minute: Number(minutes) }) <
    DateTime.now().startOf('day')
  );
};
export const getStartDateAndEndDate = (
  startTime: string,
  endTime: string,
  date: string,
): [string, string] => {
  const [hour, minutes] = startTime.split(':');
  const start_time = DateTime.fromISO(date).set({ hour: Number(hour), minute: Number(minutes) });
  const duration = calculateDuration(startTime, endTime, date, 0, 'raw', false) as number;

  const end_time = start_time.plus({ minutes: duration });

  return [start_time.toString(), end_time.toString()];
};

export const dayIsBeforeToday = (day: string): boolean => {
  const { timeZone } = getCurrentlySelectedLocation();

  const dayStr = DateTime.fromISO(day).toFormat('yyyy-MM-dd');
  const inputDay = DateTime.fromFormat(dayStr, 'yyyy-MM-dd', { zone: timeZone }).startOf('day');
  const today = DateTime.now().setZone(timeZone).startOf('day');

  return inputDay < today;
};

export const dayIsToday = (day: string): boolean => {
  const { timeZone } = getCurrentlySelectedLocation();

  const dayStr = DateTime.fromISO(day).toFormat('yyyy-MM-dd');
  const inputDay = DateTime.fromFormat(dayStr, 'yyyy-MM-dd', { zone: timeZone }).startOf('day');
  const today = DateTime.now().setZone(timeZone).startOf('day');

  return inputDay.hasSame(today, 'day');
};

export const isDateTimeBeforeNow = (date: string): boolean => {
  const { timeZone } = getCurrentlySelectedLocation();

  const now = DateTime.now().setZone(timeZone);
  const input = DateTime.fromISO(date).setZone(timeZone);

  return input < now;
};

export const isDateTimeAfterNow = (date: string): boolean => {
  const { timeZone } = getCurrentlySelectedLocation();

  const now = DateTime.now().setZone(timeZone);
  const input = DateTime.fromISO(date).setZone(timeZone);

  return input > now;
};

export const isDateTimeBeforeToday = (date: string): boolean => {
  const { timeZone } = getCurrentlySelectedLocation();

  const dayStr = DateTime.fromISO(date).toFormat('yyyy-MM-dd');
  const inputDay = DateTime.fromFormat(dayStr, 'yyyy-MM-dd', { zone: timeZone }).startOf('day');
  const today = DateTime.now().setZone(timeZone).startOf('day');

  return inputDay < today;
};

export const formatTimeFromISO = (
  isoTime: string,
  is24hTimeFormat: boolean,
  timeZone?: string,
): string => {
  const timeFormat = is24hTimeFormat ? 'HH:mm' : 'h:mm a';
  const dateTime = DateTime.fromISO(isoTime);
  const dateTimeWithZone = timeZone ? dateTime.setZone(timeZone) : dateTime;
  const formatted = dateTimeWithZone.toFormat(timeFormat);
  return is24hTimeFormat ? formatted : formatted.toLowerCase();
};

export const formatDateFromISO = (isoTime: string, timeZone?: string): string => {
  const dateTime = DateTime.fromISO(isoTime);
  const dateTimeWithZone = timeZone ? dateTime.setZone(timeZone) : dateTime;
  return dateTimeWithZone.toFormat('EEE, MMM d');
};

export const formatTimeFromISOCompact = (
  isoTime: string,
  is24hTimeFormat: boolean,
  timeZone?: string,
): string => {
  const timeFormat = is24hTimeFormat ? 'HH:mm' : 'h:mma';
  const dateTime = DateTime.fromISO(isoTime);
  const dateTimeWithZone = timeZone ? dateTime.setZone(timeZone) : dateTime;
  let formatted = dateTimeWithZone.toFormat(timeFormat);

  if (!is24hTimeFormat) {
    formatted = formatted.toLowerCase().replaceAll('m', '');
  }

  return formatted;
};

export const formatTimeRangeFromISOCompact = (
  isoStart: string,
  isoEnd: string,
  is24hTimeFormat: boolean,
  timeZone: string | undefined,
) => {
  const start = timeZone
    ? DateTime.fromISO(isoStart).setZone(timeZone)
    : DateTime.fromISO(isoStart);
  const end = timeZone ? DateTime.fromISO(isoEnd).setZone(timeZone) : DateTime.fromISO(isoEnd);

  const sameDay = start.hasSame(end, 'day');
  if (sameDay && start.hour === 0 && start.minute === 0 && end.hour === 23 && end.minute === 59) {
    return 'All day';
  }

  // Round to nearest hour
  const roundToNearestHour = (dt: DateTime) => {
    let hour = dt.hour + (dt.minute >= 30 ? 1 : 0);
    if (hour === 24) hour = 0;
    return dt.set({ hour, minute: 0, second: 0, millisecond: 0 });
  };

  const roundedStart = roundToNearestHour(start);
  const roundedEnd = roundToNearestHour(end);

  if (is24hTimeFormat) {
    return `${roundedStart.toFormat('HH:mm')} - ${roundedEnd.toFormat('HH:mm')}`;
  }

  const fmt = (dt: DateTime) => dt.toFormat('ha').toLowerCase().replaceAll('m', '');
  return `${fmt(roundedStart)} - ${fmt(roundedEnd)}`;
};
